import type { Ball } from '../entities/Ball';
import type { Brick } from '../entities/Brick';
import type { Paddle } from '../entities/Paddle';

export type FieldBounds = {
  halfWidth: number;
  top: number;
  bottom: number;
};

export type BrickHitEvent = {
  brick: Brick;
  side: 'x' | 'z';
};

export class CollisionSystem {
  resolveWalls(ball: Ball, field: FieldBounds): 'wall' | 'bottom' | null {
    if (!ball.launched) return null;
    const pos = ball.position;
    const r = ball.radius;
    let hit: 'wall' | 'bottom' | null = null;

    if (pos.x - r < -field.halfWidth) {
      pos.x = -field.halfWidth + r;
      ball.velocity.x = Math.abs(ball.velocity.x);
      hit = 'wall';
    } else if (pos.x + r > field.halfWidth) {
      pos.x = field.halfWidth - r;
      ball.velocity.x = -Math.abs(ball.velocity.x);
      hit = 'wall';
    }

    if (pos.z - r < field.top) {
      pos.z = field.top + r;
      ball.velocity.z = Math.abs(ball.velocity.z);
      hit = 'wall';
    } else if (pos.z - r > field.bottom) {
      hit = 'bottom';
    }

    return hit;
  }

  resolvePaddle(ball: Ball, paddle: Paddle): boolean {
    if (!ball.launched || ball.velocity.z <= 0) return false;

    const pos = ball.position;
    const r = ball.radius;
    const left = paddle.x - paddle.halfWidth;
    const right = paddle.x + paddle.halfWidth;
    const front = paddle.z - paddle.halfDepth;
    const back = paddle.z + paddle.halfDepth;

    if (pos.x + r < left || pos.x - r > right) return false;
    if (pos.z + r < front || pos.z - r > back) return false;

    // Catch anywhere in the paddle's box (removed the "must be above center"
    // check) so a ball that's already crossed the paddle-face plane still
    // registers as a bounce instead of tunnelling into the dropzone.
    pos.z = front - r - 0.001;
    const offset = (pos.x - paddle.x) / paddle.halfWidth;
    const speed = Math.max(8.5, Math.min(14, ball.velocity.length() * 1.02));
    const angle = offset * 1.1;
    ball.velocity.set(Math.sin(angle), 0, -Math.cos(angle)).normalize().multiplyScalar(speed);
    return true;
  }

  resolveBricks(ball: Ball, bricks: Brick[]): BrickHitEvent | null {
    if (!ball.launched) return null;
    const pos = ball.position;
    const r = ball.radius;

    for (const brick of bricks) {
      if (!brick.alive) continue;

      const left = brick.x - brick.halfWidth;
      const right = brick.x + brick.halfWidth;
      const front = brick.z - brick.halfDepth;
      const back = brick.z + brick.halfDepth;

      if (pos.x + r < left || pos.x - r > right) continue;
      if (pos.z + r < front || pos.z - r > back) continue;

      const overlapX = Math.min(pos.x + r - left, right - (pos.x - r));
      const overlapZ = Math.min(pos.z + r - front, back - (pos.z - r));

      if (overlapX < overlapZ) {
        ball.velocity.x *= -1;
        pos.x += ball.velocity.x > 0 ? overlapX : -overlapX;
        return { brick, side: 'x' };
      }

      ball.velocity.z *= -1;
      pos.z += ball.velocity.z > 0 ? overlapZ : -overlapZ;
      return { brick, side: 'z' };
    }

    return null;
  }
}
